<template>
  <div class="h-full">
    <div class="bonus-table relative h-full">
      <div class="table-header h-[34px]">
        <div class="th w-1/3 flex items-center justify-center">{{ t('vipBonusTable.level') }}</div>
        <div class="th w-1/3 flex items-center justify-center">{{ t('vipBonusTable.requiredBet') }}</div>
        <div class="th w-1/3 flex items-center justify-center">{{ t('vipBonusTable.bonus') }}</div>
      </div>
      <div class="overflow-y-auto h-[calc(100%-34px)] !text-white">
        <div v-for="(row, idx) in upgradeData" :key="idx" :class="['table-row', { zebra: idx % 2 != 1 }]">

          <div class="td" style="display: flex; align-items: center; justify-content: center;">
            <img :src="imgUrl + row.icon" alt="" class="w-[auto] h-[18px] " />
          </div>
          <div class="td" style="font-size:10px;">
            {{ numberFormat(row.requiredBet) }}
            <!-- 进度条 - 只在下一级显示 -->
            <div v-if="isNextLevel(row.level)" class="progress-container">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
              <div class="progress-text">
                {{ numberFormat(currentBet) }}/{{ numberFormat(row.requiredBet) }}
              </div>
            </div>
          </div>
          <div class="td color-[#FEF64A] " style="font-size:10px;">
            <div v-if="IS_DOUBLE_WALLET">
              <div>{{ numberFormat(row.reward) }}</div>
            </div>
            <div v-else class="flex items-center justify-center">
              <div v-if="row.reward">{{ VITE_SYMBOL }} {{ numberFormat(row.reward) }}<span v-if="row.rewardBound">({{
                  t('cash') }})</span></div>
              <div v-if="row.rewardBound" class="ml-[10px]">{{ numberFormat(row.rewardBound) }}<span
                  v-if="row.reward">({{ t('bonus') }})</span></div>
              <div v-if="!row.reward && !row.rewardBound">{{ VITE_SYMBOL }} {{ numberFormat(row.reward) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="lingqu" :class="{ 'from-person': isFromPerson }">
      <Button type="long" class="linqubtn" :disabled="!canDrawReward">{{
        t('vipBonusTable.claimUpgradeReward') }}</Button>
    </div>
    <Teleport to="body">
      <div class="lingqu pop" :class="{ 'from-person': isFromPerson }" @click="handleDrawReward">

      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { Button } from "@/components/Button"
import { ref, onMounted, computed } from 'vue'
import { numberFormat } from '@/utils/formatter'
import { useRoute } from 'vue-router'
import { useVipStore } from '@/stores/modules/vip'
import * as GameApi from '@/api/game'
import { useI18n } from 'vue-i18n'

// 定义emits
const emit = defineEmits<{
  'show-reward': [amount: string]
}>()

const { t } = useI18n()
const route = useRoute()
const imgUrl = import.meta.env.VITE_IMG_URL
const vipStore = useVipStore()

// 判断是否从个人中心跳转
const isFromPerson = computed(() => {
  return route.path === '/activity' &&
    route.query.tab === 'vip' &&
    sessionStorage.getItem('fromPerson') === 'true'
})

// 使用store中的升级奖励数据
const upgradeData = computed(() => vipStore.upgradeRewardData)
const IS_DOUBLE_WALLET = import.meta.env.VITE_WALLET_SWITCH == 'true' ? true : false;
const VITE_SYMBOL = import.meta.env.VITE_SYMBOL

// 当前等级
const currentLevel = computed(() => vipStore.vipInfo.level || 0)

// 当前投注
const currentBet = computed(() => vipStore.vipInfo.bet || 0)

// 判断是否为下一级
const isNextLevel = (level: number) => {
  return level === currentLevel.value + 1
}

// 计算进度百分比 - 显示当前等级到下一级的进度
const progressPercentage = computed(() => {
  const nextLevelRow = upgradeData.value.find(row => row.level === currentLevel.value + 1)
  if (!nextLevelRow || nextLevelRow.requiredBet === 0) return 0

  const percentage = (currentBet.value / nextLevelRow.requiredBet) * 100
  return Math.min(percentage, 100) // 确保不超过100%
})

// 检查是否可以领取升级奖励
const canDrawReward = computed(() => {
  const drawRewardList = vipStore.vipData.drawRewardList || []
  return drawRewardList.includes(1)
})

// 领取升级奖励
const drawReward = () => {
  GameApi.drawReward({ type: 1 }).then(res => {
    // 显示奖励弹窗
    const amount = res.data || '0,00'
    emit('show-reward', amount)
  }).catch(err => {
    console.error('领取失败:', err)
  })
}

// 处理领取奖励的点击事件
const handleDrawReward = () => {
  if (canDrawReward.value) {
    drawReward()
  }
}
</script>

<style scoped>
.table-header {
  display: flex;
  color: #60B289;
  font-size: 12px;
  font-weight: 500;
  gap: 0;
  /* border-top: 1px solid #009F76; */
  padding: 12px 0;
  /* background: #006849; */
}

.th {}

.table-row {
  display: flex;
  font-size: 12px;
  color: #fff;
  align-items: center;
  min-height: 34px;
}

.table-row .td {
  flex: 1;
  text-align: center;
  padding: 5px 0;
}

.table-row.zebra {
  background: rgba(8, 44, 33, 0.3);
}

.lingqu {
  position: fixed;
  bottom: 50px;
  /* 直接使用固定值，避免动态计算 */
  left: 0;
  background: linear-gradient(180deg, #00554A 0%, #082C21 100%);
  height: max(60px, env(safe-area-inset-bottom) + 130px);
  /* line-height: 80px; */
  text-align: center;
  width: 100%;
  z-index: 1;
  padding-bottom: max(60px, env(safe-area-inset-bottom) + 50px);
  /* border-top: 1px solid #009F76; */
  display: flex;
  align-items: center;
  justify-content: center;
}

.lingqu.pop {
  background: transparent;
  z-index: 1000;
  height: 60px !important;
  padding-bottom: 0 !important;
  bottom: 70px ;
}
.lingqu.pop.from-person{
  bottom: 0px !important;
  z-index: 0 !important;
}
/* 从个人中心跳转时的特殊样式 */
.lingqu.from-person {
  height: max(60px, env(safe-area-inset-bottom) + 50px) !important;
  bottom: 0 !important;
  padding-bottom: 0 !important;
  /* 定位到屏幕底部，因为底部tab被隐藏了 */
}

/* 只在真正需要的时候才使用安全区域 */
@supports (padding: max(0px)) {
  .lingqu {
    bottom: max(0px, env(safe-area-inset-bottom));
  }

  .lingqu.from-person {
    bottom: max(0px, env(safe-area-inset-bottom));
  }
}


.linqubtn {
  height: 41px;
  line-height: 41px;
  width: 80%;
  background: linear-gradient(0deg, #796F22 0%, #7D7B38 100%);
  border-radius: 7px;
  color: #ABA778;
  font-size: 12px;
  transition: all 0.3s ease;
  cursor: not-allowed;
}

.linqubtn.active {
  background: linear-gradient(0deg, #FABD63 0%, #F19C32 100%);
  color: #FEFBCB;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(254, 246, 74, 0.3);
  cursor: pointer;
}

.progress-container {
  margin-top: 8px;
  width: 100%;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(96, 178, 137, 0.3);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}

.progress-fill {
  height: 100%;
  background: #F6B552;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 10px;
  color: #60B289;
  text-align: center;
  line-height: 1;
}
</style>
